import { Metric, Step } from 'src/components/pipelines/models';
import { LoadingStatus } from 'src/constants/loading-status';
import { Action } from 'src/types/state';
import createReducer from 'src/utils/create-reducer';

import { getFeatureFlagFromStep } from '../../utils/getFeatureFlagFromStep';
import { REQUEST_METRICS } from '../actions/pipelines';

const AGENT_STEP_METRICS_ENABLED = 'agent_step-metrics-enabled';

const ONE_KILOBYTE = 1024;
const ONE_MEGABYTE = 1024 * 1024;

export type MetricsState = {
  metrics: {
    [stepUuid: string]: {
      MEMORY: { [key: string]: Metric };
      CPU: { [key: string]: Metric };
      MEMORY_PERCENTAGE: { [key: string]: Metric };
      DISK: { [key: string]: Metric };
      DISK_IOPS: { [key: string]: Metric };
      MEMORY_SPLIT: { [key: string]: Metric };
      NETWORK: { [key: string]: Metric };
    };
  };
  fetchedStatus: LoadingStatus;
};

export const initialState: MetricsState = {
  metrics: {},
  fetchedStatus: LoadingStatus.Before,
};

export const metricsReducer = createReducer(initialState, {
  [REQUEST_METRICS.REQUEST](state) {
    return {
      ...state,
      fetchedStatus: LoadingStatus.Fetching,
    };
  },
  [REQUEST_METRICS.ERROR](state) {
    return {
      ...state,
      fetchedStatus: LoadingStatus.Failed,
    };
  },
  [REQUEST_METRICS.SUCCESS](
    state: MetricsState,
    action: Action<
      {
        metric_type?:
          | 'MEMORY'
          | 'CPU'
          | 'MEMORY_PERCENTAGE'
          | 'DISK'
          | 'DISK_IOPS'
          | 'MEMORY_SPLIT'
          | 'NETWORK';
        sample_time: string;
        services?: {
          [key: string]: {
            cpu: number;
            memory: number;
            'io.riops': number;
            'io.wiops': number;
            'memory.anon': number;
            'memory.file': number;
            rx_bytes: number;
            tx_bytes: number;
            used_disk: number;
          };
        };
      }[]
    > & { meta: { step: Step } }
  ) {
    if (!action.payload || !action.meta) {
      return state;
    }

    const { step } = action.meta;
    const stepUuid = step.uuid;
    const stepMetrics = state.metrics[stepUuid] || {
      MEMORY: {},
      CPU: {},
      MEMORY_PERCENTAGE: {},
      DISK: {},
      DISK_IOPS: {},
      MEMORY_SPLIT: {},
      NETWORK: {},
    };

    const agentStepMetricsEnabled = getFeatureFlagFromStep(
      step.featureFlags,
      AGENT_STEP_METRICS_ENABLED
    );

    if (agentStepMetricsEnabled) {
      action.payload.forEach(({ sample_time, services }) => {
        if (!sample_time || !services) return;
        Object.entries(services).forEach(
          ([serviceName, service]: [
            string,
            {
              memory: number;
              cpu: number;
              'io.riops': number;
              'io.wiops': number;
              'memory.anon': number;
              'memory.file': number;
              rx_bytes: number;
              tx_bytes: number;
              used_disk: number;
            }
          ]) => {
            const {
              memory,
              cpu,
              'io.riops': diskIopsIn,
              'io.wiops': diskIopsOut,
              'memory.anon': memoryAnon,
              'memory.file': memoryFile,
              rx_bytes: receivedData,
              tx_bytes: transmittedData,
              used_disk: usedDisk,
            } = service;
            const memoryLimit = step.services.find(
              s => s.name === serviceName
            )?.['resource_limits.memory_limit_in_megabytes'];

            if (memory !== undefined) {
              stepMetrics.MEMORY[sample_time] =
                stepMetrics.MEMORY[sample_time] ||
                new Metric({
                  metric_type: 'MEMORY',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.MEMORY[sample_time].value[serviceName] = memory;
            }

            if (cpu !== undefined) {
              stepMetrics.CPU[sample_time] =
                stepMetrics.CPU[sample_time] ||
                new Metric({
                  metric_type: 'CPU',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.CPU[sample_time].value[serviceName] = cpu;
            }

            if (memoryLimit !== undefined) {
              stepMetrics.MEMORY_PERCENTAGE[sample_time] =
                stepMetrics.MEMORY_PERCENTAGE[sample_time] ||
                new Metric({
                  metric_type: 'MEMORY_PERCENTAGE',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.MEMORY_PERCENTAGE[sample_time].value[serviceName] =
                Math.round((memory / memoryLimit) * 100);
            }

            if (usedDisk !== undefined) {
              stepMetrics.DISK[sample_time] =
                stepMetrics.DISK[sample_time] ||
                new Metric({
                  metric_type: 'DISK',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.DISK[sample_time].value[serviceName] = {
                usedDisk: (usedDisk || 0) / ONE_MEGABYTE,
              };
            }

            if (diskIopsIn !== undefined || diskIopsOut !== undefined) {
              stepMetrics.DISK_IOPS[sample_time] =
                stepMetrics.DISK_IOPS[sample_time] ||
                new Metric({
                  metric_type: 'DISK_IOPS',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.DISK_IOPS[sample_time].value[serviceName] = {
                diskIopsIn: (diskIopsIn || 0) / ONE_KILOBYTE,
                diskIopsOut: (diskIopsOut || 0) / ONE_KILOBYTE,
              };
            }

            if (memoryAnon !== undefined || memoryFile !== undefined) {
              stepMetrics.MEMORY_SPLIT[sample_time] =
                stepMetrics.MEMORY_SPLIT[sample_time] ||
                new Metric({
                  metric_type: 'MEMORY_SPLIT',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.MEMORY_SPLIT[sample_time].value[serviceName] = {
                memoryAnon: memoryAnon || 0,
                memoryFile: memoryFile || 0,
              };
            }

            if (receivedData !== undefined && transmittedData !== undefined) {
              stepMetrics.NETWORK[sample_time] =
                stepMetrics.NETWORK[sample_time] ||
                new Metric({
                  metric_type: 'NETWORK',
                  value: {},
                  step: { uuid: stepUuid },
                  sample_time,
                });
              stepMetrics.NETWORK[sample_time].value[serviceName] = {
                receivedData: (receivedData || 0) / ONE_KILOBYTE,
                transmittedData: (transmittedData || 0) / ONE_KILOBYTE,
              };
            }
          }
        );
      });
    } else {
      action.payload.forEach(metric => {
        if (metric.metric_type && metric.sample_time) {
          stepMetrics[metric.metric_type][metric.sample_time] = new Metric(
            metric
          );
        }
      });
    }

    return {
      metrics: {
        ...state.metrics,
        [stepUuid]: stepMetrics,
      },
      fetchedStatus: LoadingStatus.Success,
    };
  },
});
